import React from 'react';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useApp } from '@/context/AppContext';
import { api, errText } from '@/lib/api';
import { clearDemoCode, getDemoCode } from '@/lib/demoAccess';

/**
 * Single implementation of the demo access-code prompt (GAPS S23).
 *
 * The marketing page stays fully public; only the sandbox logins are gated, and
 * the gate that matters is the API's — this is just the UI that collects the
 * code so a prospect isn't handed a bare 403. Every demo entry point uses this
 * hook so there is one place to change the copy or the flow.
 *
 *   const { launch, dialog, busyRole } = useDemoLaunch();
 *   <Button onClick={() => launch('nurse', () => navigate('/nurse'))} />
 *   {dialog}
 */
export function useDemoLaunch() {
  const { demoLogin } = useApp();
  const [accessRequired, setAccessRequired] = React.useState(false);
  const [demoMode, setDemoMode] = React.useState(false);
  const [busyRole, setBusyRole] = React.useState('');
  const [pending, setPending] = React.useState(null); // { role, onSuccess }
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState('');

  // One call serves both flags: whether to show the demo CTAs at all, and
  // whether clicking one has to collect a code first. Cached as a promise so
  // `launch` can *await* it: a prospect who clicks a demo button before the
  // response lands must still get the code prompt, not a bare error toast.
  const metaRef = React.useRef(null);
  const loadMeta = React.useCallback(() => {
    if (!metaRef.current) {
      metaRef.current = api.get('/')
        .then((r) => ({
          demoMode: !!r.data?.demo_mode,
          accessRequired: !!r.data?.demo_access_required,
        }))
        // Unreachable API: assume a code is needed. Prompting when we didn't
        // have to is recoverable; swallowing the prompt is a dead end.
        .catch(() => ({ demoMode: false, accessRequired: true }));
    }
    return metaRef.current;
  }, []);

  React.useEffect(() => {
    let alive = true;
    loadMeta().then((m) => {
      if (!alive) return;
      setDemoMode(m.demoMode);
      setAccessRequired(m.accessRequired);
    });
    return () => { alive = false; };
  }, [loadMeta]);

  const run = React.useCallback(async (role, onSuccess, typedCode, required) => {
    setBusyRole(role);
    setError('');
    try {
      await demoLogin(role, typedCode);
      setPending(null);
      setCode('');
      onSuccess?.();
      return true;
    } catch (err) {
      const status = err?.response?.status;
      // 403/429 on a gated environment means the code was wrong, missing, or
      // rate-limited — reopen the prompt rather than bouncing to /login.
      if (required && (status === 403 || status === 429)) {
        clearDemoCode();
        setPending({ role, onSuccess });
        setError(errText(err, 'That demo access code is not valid'));
      } else {
        toast.error(errText(err, 'Demo sign-in failed. Please try again.'));
      }
      return false;
    } finally {
      setBusyRole('');
    }
  }, [demoLogin]);

  const launch = React.useCallback(async (role, onSuccess) => {
    setBusyRole(role);  // held across the await so the button can't be double-fired
    const { accessRequired: required } = await loadMeta();
    if (required && !getDemoCode()) {
      setBusyRole('');
      setError('');
      setPending({ role, onSuccess });
      return false;
    }
    return run(role, onSuccess, undefined, required);
  }, [loadMeta, run]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !pending) return;
    const { accessRequired: required } = await loadMeta();
    run(pending.role, pending.onSuccess, code.trim(), required);
  };

  const dialog = (
    <Dialog open={!!pending} onOpenChange={(open) => { if (!open) { setPending(null); setError(''); } }}>
      <DialogContent className="sm:max-w-sm" data-testid="demo-access-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-primary" /> Demo access code
            </DialogTitle>
            <DialogDescription>
              The interactive demo is available to invited practices. Enter the code
              from your invitation to open the sandbox.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="demo-access-code">Access code</Label>
            <Input
              id="demo-access-code"
              data-testid="demo-access-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter your code"
              autoComplete="off"
            />
            {error ? (
              <p className="text-sm text-destructive" data-testid="demo-access-error">{error}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!code.trim() || !!busyRole} data-testid="demo-access-submit">
              {busyRole ? 'Opening…' : 'Open demo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return { launch, dialog, busyRole, accessRequired, demoMode };
}
